import { useState } from 'react';
import { Check, Copy, MapPin, Navigation, X } from 'lucide-react';

export function AddressLink({
  address,
  label,
  className = '',
}: {
  address: string;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-start gap-1 min-w-0 text-navy hover:text-gold hover:underline text-left ${className}`}
      >
        <MapPin size={13} className="flex-shrink-0 mt-0.5" />
        <span className="whitespace-normal break-words">
          {label && label !== address ? `${label} · ${address}` : address}
        </span>
      </button>
      {open && (
        <OpenInMapsModal address={address} label={label} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

interface OpenInMapsModalProps {
  address: string;
  label?: string;
  onClose: () => void;
}

function mapsUrls(address: string) {
  const q = encodeURIComponent(address);
  return {
    apple: `https://maps.apple.com/?q=${q}`,
    google: `https://www.google.com/maps/search/?api=1&query=${q}`,
    waze: `https://waze.com/ul?q=${q}&navigate=yes`,
  };
}

export default function OpenInMapsModal({ address, label, onClose }: OpenInMapsModalProps) {
  const [copied, setCopied] = useState(false);
  const urls = mapsUrls(address);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — nothing to do.
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="open-in-maps-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 pb-3">
          <div className="min-w-0 pr-3">
            <h3 id="open-in-maps-title" className="text-lg font-semibold text-navy">
              Open in maps
            </h3>
            {label && (
              <p className="text-sm font-medium text-gray-800 mt-1 truncate">{label}</p>
            )}
            <p className="text-sm text-gray-500 mt-0.5 break-words">{address}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 pointer-coarse:p-2.5 pointer-coarse:-m-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-5 pb-5 space-y-2">
          <a
            href={urls.apple}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-navy hover:bg-off-white transition text-navy font-medium"
          >
            <MapPin size={18} className="text-gold flex-shrink-0" />
            Apple Maps
          </a>
          <a
            href={urls.google}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-navy hover:bg-off-white transition text-navy font-medium"
          >
            <MapPin size={18} className="text-gold flex-shrink-0" />
            Google Maps
          </a>
          <a
            href={urls.waze}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-navy hover:bg-off-white transition text-navy font-medium"
          >
            <Navigation size={18} className="text-gold flex-shrink-0" />
            Waze
          </a>
          <button
            type="button"
            onClick={copyAddress}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-navy hover:bg-off-white transition text-navy font-medium"
          >
            {copied ? (
              <Check size={18} className="text-gold flex-shrink-0" />
            ) : (
              <Copy size={18} className="text-gold flex-shrink-0" />
            )}
            {copied ? 'Copied!' : 'Copy address'}
          </button>
        </div>
      </div>
    </div>
  );
}
